import React from 'react';
import { Input } from 'reactstrap';
import {
    TermForm,
    TermFormLocation,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import {
    getOptionsForAnswerType,
    LoiFormAnswerType,
} from '../../../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';
import { TermsFormSelect } from './TermsFormSelect';
import { validatePercentageField } from '../../../../../../utils/percentFieldValidator';

export interface TermsFormLeaseTermsBlockProps {
    termForm: TermForm;
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    rowComments?: (string | null)[];
    onRowCommentChange?: (rowIndex: number, comment: string | null) => void;
}

interface LeaseTermsRowProps {
    location: TermFormLocation;
    readOnly: boolean;
    showLocationColumn: boolean;
    errorPrefix: string;
    errors?: Set<string>;
    showCommentColumn: boolean;
    comment: string | null;
    isBuyerReview?: boolean;
    onFieldChange: (patch: Partial<TermFormLocation>) => void;
    onCommentChange: (comment: string | null) => void;
}

const LeaseTermsRow = ({
    location,
    readOnly,
    showLocationColumn,
    errorPrefix,
    errors,
    showCommentColumn,
    comment,
    isBuyerReview,
    onFieldChange,
    onCommentChange,
}: LeaseTermsRowProps) => (
    <tr>
        {showLocationColumn && (
            <td className="terms-form-col-item table-label-cell">{location.locationName}</td>
        )}
        <td className="terms-form-col-value">
            <Input
                type="text"
                value={location.termOfLease ?? ''}
                disabled={readOnly}
                onChange={(e) => onFieldChange({ termOfLease: e.target.value || null })}
                className="form-control loi-form-input"
                aria-label="Term of Lease"
                invalid={errors?.has(`${errorPrefix}termOfLease`)}
            />
        </td>
        <td className="terms-form-col-value">
            <TermsFormSelect
                value={location.leaseType ?? ''}
                options={getOptionsForAnswerType(LoiFormAnswerType.LeaseType)}
                disabled={readOnly}
                onChange={(v) => onFieldChange({ leaseType: v || null })}
                aria-label="Lease Type"
                invalid={errors?.has(`${errorPrefix}leaseType`)}
            />
        </td>
        <td className="terms-form-col-value">
            <Input
                type="text"
                value={
                    location.leaseRate != null && location.leaseRate !== ''
                        ? `${location.leaseRate}%`
                        : ''
                }
                disabled={readOnly}
                onKeyDown={(e) => {
                    if (validatePercentageField(e, (location.leaseRate ?? '').toString())) {
                        e.preventDefault();
                    }
                }}
                onChange={(e) => {
                    const value = e.target.value.replace('%', '');
                    onFieldChange({ leaseRate: value === '' ? null : value });
                }}
                className="form-control loi-form-input"
                aria-label="Lease Rate"
                invalid={errors?.has(`${errorPrefix}leaseRate`)}
                style={{ width: '80px' }}
            />
        </td>
        <td className="terms-form-col-value">
            <Input
                type="text"
                value={
                    location.leaseRateEscalation != null && location.leaseRateEscalation !== ''
                        ? `${location.leaseRateEscalation}%`
                        : ''
                }
                disabled={readOnly}
                onKeyDown={(e) => {
                    if (
                        validatePercentageField(
                            e,
                            (location.leaseRateEscalation ?? '').toString()
                        )
                    ) {
                        e.preventDefault();
                    }
                }}
                onChange={(e) => {
                    const value = e.target.value.replace('%', '');
                    onFieldChange({ leaseRateEscalation: value === '' ? null : value });
                }}
                className="form-control loi-form-input loi-form-percent-input"
                aria-label="Lease Rate Escalation"
                invalid={errors?.has(`${errorPrefix}leaseRateEscalation`)}
            />
        </td>
        <td className="terms-form-col-value">
            <TermsFormSelect
                value={location.leaseRateUponExtension ?? ''}
                options={getOptionsForAnswerType(LoiFormAnswerType.LeaseRateUponExtension)}
                disabled={readOnly}
                onChange={(e) => onFieldChange({ leaseRateUponExtension: e })}
                aria-label="Lease Rate Upon Extension"
                invalid={errors?.has(`${errorPrefix}leaseRateUponExtension`)}
            />
        </td>
        {showCommentColumn && (
            <td className="terms-form-col-comment">
                <Input
                    type="textarea"
                    value={comment ?? ''}
                    readOnly={!isBuyerReview}
                    onChange={(e) =>
                        onCommentChange(e.target.value === '' ? null : e.target.value)
                    }
                    className="terms-form-comment-input"
                    aria-label="Comment for Lease Terms"
                />
            </td>
        )}
    </tr>
);

export const TermsFormLeaseTermsBlock: React.FC<TermsFormLeaseTermsBlockProps> = ({
    termForm,
    readOnly = false,
    onChange,
    errors,
    isBuyerReview,
    isBuyerReviewConfirmed,
    rowComments,
    onRowCommentChange,
}) => {
    const qualifyingLocations = (termForm.locations ?? []).filter(
        (l) => l.leaseAssumption === 'new'
    );

    const showLocationColumn = (termForm.locations ?? []).length >= 2;
    const showCommentColumn = isBuyerReview || !!isBuyerReviewConfirmed;

    const handleFieldChange = (locationId: string, patch: Partial<TermFormLocation>) => {
        const updatedLocations = (termForm.locations ?? []).map((l) =>
            l.locationId === locationId ? { ...l, ...patch } : l
        );
        onChange?.({ ...termForm, locations: updatedLocations });
    };

    if (qualifyingLocations.length === 0) return null;

    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-lease-terms-block">
            <div className="terms-form-block-name">Lease Terms</div>
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            {showLocationColumn && <th></th>}
                            <th>Term of Lease</th>
                            <th>Lease Type</th>
                            <th>Lease Rate</th>
                            <th>Lease Rate Escalation</th>
                            <th>Lease Rate Upon Extension</th>
                            {showCommentColumn && (
                                <th className="terms-form-col-comment">Comments</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {qualifyingLocations.map((loc, index) => (
                            <LeaseTermsRow
                                key={loc.locationId}
                                location={loc}
                                readOnly={readOnly}
                                showLocationColumn={showLocationColumn}
                                errorPrefix={`lease:${loc.locationId}:`}
                                errors={errors}
                                showCommentColumn={showCommentColumn}
                                comment={rowComments?.[index] ?? null}
                                isBuyerReview={isBuyerReview}
                                onFieldChange={(patch) => handleFieldChange(loc.locationId, patch)}
                                onCommentChange={(comment) =>
                                    onRowCommentChange?.(index, comment)
                                }
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
